import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, InputGroup, Form,
    Table, Badge, Nav, Tab, ProgressBar, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { formatDate, formatDateTime, formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function Tracabilite() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [historique, setHistorique] = useState(null);
    const [stats, setStats] = useState(null);
    const [lots, setLots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('chrono');
    const [filtres, setFiltres] = useState({
        date_debut: '',
        date_fin: '',
        type: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    const handleSearch = async () => {
        if (!searchTerm.trim()) return;
        
        setSearching(true);
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=rechercher&q=${encodeURIComponent(searchTerm)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur recherche");
            
            const result = await response.json();
            setSearchResults(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setSearching(false);
        }
    };

    const handleSelectProduct = async (produit) => {
        setSelectedProduct(produit);
        setSearchResults([]);
        setSearchTerm('');
        await loadHistorique(produit.id_produit);
        await loadStats(produit.id_produit);
        await loadLots(produit.id_produit);
    };

    const loadHistorique = async (id_produit) => {
        setLoading(true);
        
        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/tracabilite.php?action=historique&id_produit=${id_produit}`;
            
            const params = new URLSearchParams();
            if (filtres.date_debut) params.append('date_debut', filtres.date_debut);
            if (filtres.date_fin) params.append('date_fin', filtres.date_fin);
            if (filtres.type) params.append('type', filtres.type);
            
            if (params.toString()) url += '&' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setHistorique(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=stats&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const loadLots = async (id_produit) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/tracabilite.php?action=lots&id_produit=${id_produit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setLots(result.data || []);
            }
        } catch (err) {
            console.error("Erreur lots", err);
        }
    };

    const applyFilters = () => {
        if (selectedProduct) {
            loadHistorique(selectedProduct.id_produit);
        }
    };

    const resetFilters = () => {
        setFiltres({
            date_debut: '',
            date_fin: '',
            type: ''
        });
        if (selectedProduct) {
            loadHistorique(selectedProduct.id_produit);
        }
    };

    const getTypeBadge = (type) => {
        const configs = {
            entree: { color: '#10b981', icon: 'ri-arrow-down-line', label: 'Entrée' },
            sortie: { color: '#ef4444', icon: 'ri-arrow-up-line', label: 'Sortie' },
            inventaire: { color: '#0ea5e9', icon: 'ri-survey-line', label: 'Inventaire' },
            commande_fournisseur: { color: '#21ACCD', icon: 'ri-truck-line', label: 'Commande fournisseur' },
            facture: { color: '#f59e0b', icon: 'ri-file-list-line', label: 'Facture' },
            bon_livraison: { color: '#64748b', icon: 'ri-truck-line', label: 'Bon de livraison' }
        };
        const config = configs[type] || { color: '#64748b', icon: 'ri-question-line', label: type };
        
        return (
            <span className="status-badge badge-sm" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon}`}></i>
                {config.label}
            </span>
        );
    };

    const getChronologie = () => {
        if (!historique) return [];
        
        const evenements = [];
        
        historique.mouvements?.forEach(m => {
            evenements.push({
                date: m.date_mouvement,
                type: 'mouvement',
                sous_type: m.type_mouvement,
                titre: `${m.type_mouvement === 'entree' ? '➕' : '➖'} Mouvement de stock`,
                description: `${m.type_mouvement === 'entree' ? 'Entrée' : 'Sortie'} de ${m.quantite} unités`,
                document: m.reference_document,
                document_type: m.type_document,
                quantite: m.quantite,
                stock_apres: m.stock_apres,
                utilisateur: m.utilisateur,
                couleur: m.type_mouvement === 'entree' ? '#10b981' : '#ef4444',
                icone: m.type_mouvement === 'entree' ? 'ri-arrow-down-line' : 'ri-arrow-up-line',
                id: m.id_mouvement
            });
        });

        historique.commandes?.forEach(c => {
            evenements.push({
                date: c.date_commande,
                type: 'document',
                sous_type: 'commande',
                titre: 'Commande fournisseur',
                description: `Commande chez ${c.tiers_nom}`,
                document: c.numero_commande,
                document_type: 'commande_fournisseur',
                quantite: c.quantite,
                tiers: c.tiers_nom,
                couleur: '#21ACCD',
                icone: 'ri-truck-line',
                id: c.id_commande_fournisseur
            });
        });

        historique.factures?.forEach(f => {
            evenements.push({
                date: f.date_facture,
                type: 'document',
                sous_type: 'facture',
                titre: 'Facture client',
                description: `Facture pour ${f.tiers_nom}`,
                document: f.numero_facture,
                document_type: 'facture',
                quantite: f.quantite,
                tiers: f.tiers_nom,
                couleur: '#f59e0b',
                icone: 'ri-file-list-line',
                id: f.id_facture
            });
        });

        historique.bons_livraison?.forEach(b => {
            evenements.push({
                date: b.date_document,
                type: 'document',
                sous_type: 'bl',
                titre: 'Bon de livraison',
                description: `Livraison pour ${b.tiers_nom}`,
                document: b.numero_bl,
                document_type: 'bon_livraison',
                quantite: b.quantite,
                tiers: b.tiers_nom,
                lot: b.lot,
                date_peremption: b.date_peremption,
                couleur: '#64748b',
                icone: 'ri-truck-line',
                id: b.id_bon_livraison
            });
        });

        historique.inventaires?.forEach(i => {
            evenements.push({
                date: i.date_inventaire,
                type: 'inventaire',
                titre: 'Inventaire',
                description: `Écart: ${i.ecart > 0 ? '+' : ''}${i.ecart}`,
                document: i.reference_inventaire,
                document_type: 'inventaire',
                quantite_theorique: i.quantite_theorique,
                quantite_reelle: i.quantite_reelle,
                ecart: i.ecart,
                utilisateur: i.utilisateur,
                couleur: i.ecart === 0 ? '#10b981' : i.ecart > 0 ? '#0ea5e9' : '#ef4444',
                icone: 'ri-survey-line',
                id: i.id_inventaire
            });
        });

        return evenements.sort((a, b) => new Date(b.date) - new Date(a.date));
    };

    const chronologie = getChronologie();

    if (error && !selectedProduct) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={handleSearch}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <Button 
                            variant="link" 
                            className="p-0 text-muted text-decoration-none" 
                            onClick={() => navigate('/stock/etat/instantane')}
                        >
                            <i className="ri-arrow-left-line me-1"></i>Stock
                        </Button>
                        <i className="ri-arrow-right-s-line text-muted"></i>
                        <span className="text-primary">Traçabilité</span>
                    </div>

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-search-eye-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Traçabilité</h4>
                                    <p className="text-muted small mb-0">Suivez le cycle de vie complet de vos produits</p>
                                </div>
                            </div>
                        </div>
                        {selectedProduct && (
                            <div className="d-flex gap-2 mt-3 mt-md-0">
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => setSelectedProduct(null)}
                                    className="rounded-pill px-3 d-flex align-items-center gap-1"
                                    size="sm"
                                >
                                    <i className="ri-close-line"></i>
                                    Changer de produit
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Barre de recherche */}
                    {!selectedProduct && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={10}>
                                        <div className="position-relative">
                                            <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            <Form.Control
                                                type="text"
                                                placeholder="Rechercher par référence, désignation ou code-barres..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={(e) => handleSearch()}
                                                className="ps-5 py-3 rounded-pill"
                                            />
                                        </div>
                                    </Col>
                                    <Col md={2}>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleSearch}
                                            disabled={searching}
                                            className="w-100 rounded-pill py-2"
                                            style={{ background: '#21ACCD', border: 'none' }}
                                        >
                                            {searching ? <Spinner size="sm" /> : 'Rechercher'}
                                        </Button>
                                    </Col>
                                </Row>

                                {/* Résultats de recherche */}
                                {searchResults.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="fw-semibold mb-3">Résultats de recherche</h6>
                                        <Row className="g-3">
                                            {searchResults.map(p => (
                                                <Col md={6} key={p.id_produit}>
                                                    <div 
                                                        className="product-card p-3 rounded-3"
                                                        onClick={() => handleSelectProduct(p)}
                                                        style={{ 
                                                            cursor: 'pointer', 
                                                            transition: 'all 0.2s ease',
                                                            background: 'white',
                                                            border: '1px solid #e2e8f0',
                                                            borderRadius: '12px'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.08)';
                                                            e.currentTarget.style.borderColor = '#21ACCD';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = 'none';
                                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                                        }}
                                                    >
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="avatar-icon">
                                                                <i className="ri-barcode-line"></i>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                                    <div>
                                                                        <h6 className="mb-1 fw-semibold">{p.designation}</h6>
                                                                        <div className="d-flex gap-2">
                                                                            <span className="reference-badge">{p.reference}</span>
                                                                            {p.nom_categorie && (
                                                                                <Badge className="rounded-pill px-2" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                                                    {p.nom_categorie}
                                                                                </Badge>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <Badge className="rounded-pill px-3" style={{ background: '#21ACCD' }}>
                                                                        Stock: {p.stock_actuel || 0}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                )}

                                {searchTerm && searchResults.length === 0 && !searching && (
                                    <div className="text-center py-4">
                                        <i className="ri-search-line fs-48 text-muted mb-3 d-block"></i>
                                        <h6>Aucun produit trouvé</h6>
                                        <p className="text-muted small">Essayez avec d'autres mots-clés</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Vue détaillée du produit sélectionné */}
                    {selectedProduct && (
                        <>
                            {/* En-tête produit */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <Row className="align-items-center">
                                        <Col md={8}>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="avatar-icon-large">
                                                    <i className="ri-barcode-line"></i>
                                                </div>
                                                <div>
                                                    <h2 className="fw-bold mb-2">{selectedProduct.designation}</h2>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        <span className="reference-badge">
                                                            <i className="ri-barcode-line me-1"></i>
                                                            {selectedProduct.reference}
                                                        </span>
                                                        {selectedProduct.nom_categorie && (
                                                            <span className="reference-badge">
                                                                <i className="ri-folder-line me-1"></i>
                                                                {selectedProduct.nom_categorie}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col md={4} className="text-md-end mt-3 mt-md-0">
                                            <div className="d-flex justify-content-md-end gap-4">
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Stock actuel</small>
                                                    <h3 className="text-primary mb-0">{selectedProduct.stock_actuel || 0}</h3>
                                                </div>
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Valeur</small>
                                                    <h3 className="text-success mb-0" style={{ fontSize: '1.2rem' }}>
                                                        {formatCurrency((selectedProduct.stock_actuel || 0) * selectedProduct.prix_achat_ht)}
                                                    </h3>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Statistiques KPI */}
                            {stats && (
                                <Row className="g-3 mb-4">
                                    <Col sm={6} xl={3}>
                                        <div className="kpi-card kpi-primary">
                                            <div className="kpi-icon">
                                                <i className="ri-stack-line fs-24"></i>
                                            </div>
                                            <div className="kpi-content">
                                                <span className="kpi-label">Total mouvements</span>
                                                <h3 className="kpi-value">{stats.total?.total_mouvements || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={6} xl={3}>
                                        <div className="kpi-card kpi-success">
                                            <div className="kpi-icon">
                                                <i className="ri-arrow-down-line fs-24"></i>
                                            </div>
                                            <div className="kpi-content">
                                                <span className="kpi-label">Entrées</span>
                                                <h3 className="kpi-value">{stats.total?.entrees || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={6} xl={3}>
                                        <div className="kpi-card kpi-danger">
                                            <div className="kpi-icon">
                                                <i className="ri-arrow-up-line fs-24"></i>
                                            </div>
                                            <div className="kpi-content">
                                                <span className="kpi-label">Sorties</span>
                                                <h3 className="kpi-value">{stats.total?.sorties || 0}</h3>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={6} xl={3}>
                                        <div className="kpi-card kpi-info">
                                            <div className="kpi-icon">
                                                <i className="ri-calendar-line fs-24"></i>
                                            </div>
                                            <div className="kpi-content">
                                                <span className="kpi-label">Premier mouvement</span>
                                                <h6 className="kpi-value" style={{ fontSize: '0.9rem' }}>
                                                    {stats.premier_mouvement?.premier_mouvement 
                                                        ? formatDate(stats.premier_mouvement.premier_mouvement)
                                                        : '-'}
                                                </h6>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Filtres */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-semibold mb-0">
                                            <i className="ri-filter-line me-2" style={{ color: '#21ACCD' }}></i>
                                            Filtres
                                        </h6>
                                        <Button
                                            variant="link"
                                            className="text-muted p-0 text-decoration-none"
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <i className={`ri-arrow-${showFilters ? 'up' : 'down'}-s-line me-1`}></i>
                                            {showFilters ? 'Masquer' : 'Afficher'}
                                        </Button>
                                    </div>
                                    
                                    {showFilters && (
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-semibold">Date début</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={filtres.date_debut}
                                                        onChange={(e) => setFiltres({...filtres, date_debut: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-semibold">Date fin</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={filtres.date_fin}
                                                        onChange={(e) => setFiltres({...filtres, date_fin: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="small fw-semibold">Type</Form.Label>
                                                    <Form.Select
                                                        value={filtres.type}
                                                        onChange={(e) => setFiltres({...filtres, type: e.target.value})}
                                                        className="border-2 rounded-3"
                                                    >
                                                        <option value="">Tous</option>
                                                        <option value="entree">Entrées</option>
                                                        <option value="sortie">Sorties</option>
                                                        <option value="inventaire">Inventaires</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3} className="d-flex align-items-end">
                                                <div className="d-flex gap-2 w-100">
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={applyFilters} 
                                                        className="flex-grow-1 rounded-pill"
                                                        style={{ background: '#21ACCD', border: 'none' }}
                                                    >
                                                        <i className="ri-filter-line me-2"></i>
                                                        Appliquer
                                                    </Button>
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={resetFilters}
                                                        className="rounded-pill"
                                                    >
                                                        <i className="ri-refresh-line"></i>
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Onglets */}
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-bottom pt-3 pb-0">
                                    <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="tracabilite-tabs">
                                        <Nav.Item>
                                            <Nav.Link eventKey="chrono">
                                                <i className="ri-timeline-line me-2"></i>
                                                Chronologie
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="mouvements">
                                                <i className="ri-stack-line me-2"></i>
                                                Mouvements
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="documents">
                                                <i className="ri-file-list-line me-2"></i>
                                                Documents
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="lots">
                                                <i className="ri-barcode-line me-2"></i>
                                                Lots & Séries
                                                {lots.length > 0 && (
                                                    <Badge className="ms-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                                        {lots.length}
                                                    </Badge>
                                                )}
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Tab.Content>
                                        {/* Chronologie - version simplifiée */}
                                        <Tab.Pane active={activeTab === 'chrono'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                                </div>
                                            ) : chronologie.length > 0 ? (
                                                <div className="chronology-list">
                                                    {chronologie.map((event, index) => (
                                                        <div key={index} className="chronology-item mb-3">
                                                            <div className="d-flex gap-3">
                                                                <div className="chronology-icon">
                                                                    <i className={`${event.icone} text-white`}></i>
                                                                </div>
                                                                <div className="chronology-content flex-grow-1">
                                                                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                                                        <div>
                                                                            <h6 className="mb-1">{event.titre}</h6>
                                                                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                                                                {getTypeBadge(event.sous_type || event.type)}
                                                                                <small className="text-muted">
                                                                                    <i className="ri-time-line me-1"></i>
                                                                                    {formatDateTime(event.date)}
                                                                                </small>
                                                                            </div>
                                                                        </div>
                                                                        {event.document && (
                                                                            <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                                                {event.document}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-muted mt-2 mb-0 small">{event.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-history-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun événement trouvé</h5>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Mouvements - version simplifiée */}
                                        <Tab.Pane active={activeTab === 'mouvements'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                                </div>
                                            ) : historique?.mouvements?.length > 0 ? (
                                                <div className="table-responsive">
                                                    <Table hover className="align-middle mb-0 custom-table">
                                                        <thead className="bg-light">
                                                            <tr>
                                                                <th className="ps-4">Date</th>
                                                                <th>Type</th>
                                                                <th className="text-end">Quantité</th>
                                                                <th className="text-center">Stock après</th>
                                                                <th>Référence</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {historique.mouvements.map((m, index) => (
                                                                <tr key={m.id_mouvement} className="timeline-row">
                                                                    <td className="ps-4"><small>{formatDateTime(m.date_mouvement)}</small></td>
                                                                    <td>{getTypeBadge(m.type_mouvement)}</td>
                                                                    <td className="text-end fw-bold">
                                                                        <span className={m.type_mouvement === 'entree' ? 'text-success' : 'text-danger'}>
                                                                            {m.type_mouvement === 'entree' ? '+' : '-'}{m.quantite}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center fw-bold text-primary">{m.stock_apres}</td>
                                                                    <td><small className="text-muted">{m.reference_document || '-'}</small></td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-stack-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun mouvement trouvé</h5>
                                                </div>
                                            )}
                                        </Tab.Pane>

                                        {/* Documents - version simplifiée */}
                                        <Tab.Pane active={activeTab === 'documents'}>
                                            {loading ? (
                                                <div className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" style={{ color: '#21ACCD' }} />
                                                </div>
                                            ) : (
                                                <>
                                                    {historique?.commandes?.length > 0 && (
                                                        <div className="mb-4">
                                                            <h6 className="fw-semibold mb-3">
                                                                <i className="ri-truck-line me-2" style={{ color: '#21ACCD' }}></i>
                                                                Commandes fournisseurs
                                                            </h6>
                                                            <Table hover className="align-middle mb-0 custom-table">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th className="ps-4">N° Commande</th>
                                                                        <th>Date</th>
                                                                        <th>Fournisseur</th>
                                                                        <th className="text-end">Quantité</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {historique.commandes.map((c, i) => (
                                                                        <tr key={i}>
                                                                            <td className="ps-4">
                                                                                <Link to={`/achats/commandes/${c.id_commande_fournisseur}`} className="table-link">
                                                                                    {c.numero_commande}
                                                                                </Link>
                                                                            </td>
                                                                            <td>{formatDate(c.date_commande)}</td>
                                                                            <td>{c.tiers_nom}</td>
                                                                            <td className="text-end fw-bold text-success">{c.quantite}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Tab.Pane>

                                        {/* Lots */}
                                        <Tab.Pane active={activeTab === 'lots'}>
                                            {lots.length > 0 ? (
                                                <Row className="g-4">
                                                    {lots.map((lot, index) => {
                                                        const estPerime = lot.date_peremption && new Date(lot.date_peremption) < new Date();
                                                        const joursRestant = lot.date_peremption 
                                                            ? Math.ceil((new Date(lot.date_peremption) - new Date()) / (1000 * 60 * 60 * 24))
                                                            : null;
                                                        
                                                        return (
                                                            <Col md={6} key={index}>
                                                                <Card className="border-0 shadow-sm rounded-3 h-100">
                                                                    <Card.Body className="p-4">
                                                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                                                            <Badge className="rounded-pill px-3 py-2" style={{ background: '#21ACCD' }}>
                                                                                <i className="ri-barcode-line me-1"></i>
                                                                                Lot: {lot.lot}
                                                                            </Badge>
                                                                            <Badge className="rounded-pill px-3" style={{ background: '#e2f3f5', color: '#fff' }}>
                                                                                {lot.nb_lignes} mouvement(s)
                                                                            </Badge>
                                                                        </div>
                                                                        
                                                                        <div className="mb-3">
                                                                            <div className="d-flex justify-content-between mb-2">
                                                                                <span className="text-muted">Quantité totale</span>
                                                                                <span className="fw-bold text-primary">{lot.quantite_totale}</span>
                                                                            </div>
                                                                            {lot.date_peremption && (
                                                                                <>
                                                                                    <div className="d-flex justify-content-between mb-2">
                                                                                        <span className="text-muted">Date péremption</span>
                                                                                        <span className={`fw-bold ${estPerime ? 'text-danger' : ''}`}>
                                                                                            {formatDate(lot.date_peremption)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="d-flex justify-content-between">
                                                                                        <span className="text-muted">Statut</span>
                                                                                        {estPerime ? (
                                                                                            <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}>
                                                                                                <i className="ri-alert-line me-1 fs-10"></i>
                                                                                                Périmé
                                                                                            </span>
                                                                                        ) : joursRestant && joursRestant <= 30 ? (
                                                                                            <span className="status-badge" style={{ background: '#f59e0b15', color: '#f59e0b' }}>
                                                                                                <i className="ri-time-line me-1 fs-10"></i>
                                                                                                Expire dans {joursRestant}j
                                                                                            </span>
                                                                                        ) : (
                                                                                            <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}>
                                                                                                <i className="ri-check-line me-1 fs-10"></i>
                                                                                                Valide
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <i className="ri-barcode-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun lot ou numéro de série</h5>
                                                    <p className="text-muted">Ce produit n'a pas de traçabilité par lot</p>
                                                </div>
                                            )}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-danger { border-left-color: #ef4444; }
                .kpi-info { border-left-color: #0ea5e9; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0ea5e9; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                
                /* Avatar Icon */
                .avatar-icon {
                    width: 48px;
                    height: 48px;
                    background: #e2f3f5;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                .avatar-icon-large {
                    width: 64px;
                    height: 64px;
                    background: #e2f3f5;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #21ACCD;
                }
                .avatar-icon-large i {
                    font-size: 32px;
                }
                
                /* Reference Badge */
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                   
                    
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Tracabilite Tabs */
                .tracabilite-tabs {
                    border-bottom: 2px solid #e2e8f0;
                }
                .tracabilite-tabs .nav-link {
                    border: none;
                    color: #64748b;
                    font-weight: 500;
                    padding: 0.75rem 1.25rem;
                    transition: all 0.2s ease;
                    background: transparent;
                }
                .tracabilite-tabs .nav-link:hover {
                    color: #21ACCD;
                    background: rgba(33, 172, 205, 0.05);
                }
                .tracabilite-tabs .nav-link.active {
                    color: #21ACCD;
                    background: transparent;
                    border-bottom: 2px solid #21ACCD;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Chronology */
                .chronology-item {
                    border-left: 2px solid #21ACCD;
                    padding-left: 1rem;
                }
                .chronology-icon {
                    width: 32px;
                    height: 32px;
                    background: #21ACCD;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .chronology-icon i {
                    font-size: 1rem;
                }
                
                .timeline-row {
                    transition: all 0.2s ease;
                }
                .timeline-row:hover {
                    background: #f8fafc;
                }
                
                .product-card {
                    transition: all 0.3s ease;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}